import React, { FC } from 'react';

import { Spinner } from '../../components/Spinner';
import { JourneyPicker } from '../JourneyPicker';
import SectionContainer from '../../components/SectionContainer';
import { List } from './List';
import { PageTitle, BodySmallMuted } from '../../components/Typography';

import { ListPageProps } from './list.types';
import {
  BackgroundWrapper,
  BannerTitle,
  BannerContentsWrapper,
  NotFoundWrapper,
  PageTitleDescriptionWrapper,
  SectionDescription,
  SolidColorBackground,
  StyledHero,
  StyledSectionContainer,
  TitleContent,
} from './ListPage.styles';
import { some } from '@utils/some';
import { pick } from 'lodash';
import { JourneyPickerProps } from '../JourneyPicker/JourneyPicker.types';
import { BackgroundImgixWrapper } from '@common/components';
import { FulfilablesLandingPagePicker } from '../fulfilableProducts/components/FulfilablesLandingPagePicker/FulfilablesLandingPagePicker';

export const ListPage: FC<ListPageProps> = ({
  journeyParams,
  onChangeJourneyParams,
  title,
  description,
  items,
  isFetching,
  errorMessage,
  displayAsLandingPage,
  displayWithBriefDescription,
  landingPageBannerImageUrl,
  acceptPromoRateCode,
  hasMoreItems,
  onShowMoreItems,
  journeyPickerProps,
  renderMetadataRow,
}) => {
  // Journey picker shows as a guests picker by default in this component
  // Don't do this if one of the other picker types have been passed
  const showGuestsPickerByDefault = !some(
    Object.values(
      pick(
        journeyPickerProps || ({} as JourneyPickerProps),
        'showCategoriesPicker',
        'showDatesPicker',
        'showSearchInput'
      )
    ),
    (x) => x
  );

  return (
    <>
      <Spinner data-cy="spinner" isActive={isFetching} showDelayMessage />

      {displayAsLandingPage ? (
        <BackgroundWrapper>
          <BackgroundImgixWrapper src={landingPageBannerImageUrl || ''}>
            <StyledHero>
              {!landingPageBannerImageUrl && <SolidColorBackground />}

              <StyledSectionContainer>
                <BannerContentsWrapper>
                  <TitleContent>
                    <BannerTitle>{title}</BannerTitle>

                    <FulfilablesLandingPagePicker
                      categories={journeyPickerProps?.categories}
                      initialSelectedCategory={journeyPickerProps?.selectedCategory || null}
                    />
                  </TitleContent>
                </BannerContentsWrapper>
              </StyledSectionContainer>
            </StyledHero>
          </BackgroundImgixWrapper>
        </BackgroundWrapper>
      ) : (
        <SectionContainer data-cy="section-container" zIndex={10}>
          <JourneyPicker
            data-cy="journey-picker"
            showGuestsPicker={showGuestsPickerByDefault}
            onChange={onChangeJourneyParams}
            acceptPromoRateCode={acceptPromoRateCode}
            {...(journeyPickerProps || {})}
            journeyParams={journeyParams}
          />
        </SectionContainer>
      )}

      <SectionContainer zIndex={5}>
        <PageTitleDescriptionWrapper data-cy="page-title-description">
          <PageTitle data-cy="page-title">{title}</PageTitle>
          <SectionDescription data-cy="section-description">{description}</SectionDescription>
        </PageTitleDescriptionWrapper>

        <List
          data-cy="list"
          items={items}
          hasMoreItems={hasMoreItems}
          onShowMoreItems={onShowMoreItems}
          renderMetadataRow={renderMetadataRow}
          displayWithBriefDescription={displayWithBriefDescription}
        />

        {!some(items) && errorMessage && (
          <NotFoundWrapper data-cy="not-found-wrapper">
            {errorMessage.title}
            <br />
            <BodySmallMuted data-cy="error-message">{errorMessage.message}</BodySmallMuted>
          </NotFoundWrapper>
        )}
      </SectionContainer>
    </>
  );
};
